import * as Yup from "yup";

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { Field, Form, Formik, useFormikContext } from "formik";
import React, { forwardRef, useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";

import { Autocomplete as FormikAutocomplete } from "formik-material-ui-lab";
import { KeyboardDatePicker as FormikKeyboardDatePicker } from "formik-material-ui-pickers";
import { TextField as FormikTextField } from "formik-material-ui";
import { createShipRecordDetailsService } from "../../../../services/shipRecordDetailsService";
import dayjs from "dayjs";
import { fetchLeaveReasons } from "../../../../services/leaveReasonsService";
import { fetchPortNames } from "../../../../services/portNamesServices";
import { fetchSailorSpecialization } from "../../../../services/sailorSpecializationService";
import useLocale from "../../../../hooks/useLocale";

const useStyles = makeStyles({
  searchBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  searchButton: {
    width: "80%",
    height: "100%",
    fontSize: "1rem",
  },
  hideButton: {
    display: "none",
  },
});

const AddSailorDetails = (
  { selectedSailor, setError, close, crewlist, finishStepper, refetch },
  ref
) => {
  const classes = useStyles();
  const { t } = useLocale();

  const sailorFormRequireAllValidation = Yup.object().shape({
    start_dt: Yup.date().nullable().required(t("Field is required")),
    leave_dt: Yup.date().nullable().required(t("Field is required")),
    specialty: Yup.object({
      id: Yup.string().required(t("Field is required")).nullable(),
    })
      .typeError(t("Field is required"))
      .required(t("Field is required")),

    leave_reason: Yup.object({
      id: Yup.string().required(t("Field is required")).nullable(),
    })
      .typeError(t("Field is required"))
      .required(t("Field is required")),
    leave_place: Yup.object({
      id: Yup.string().required(t("Field is required")).nullable(),
    })
      .typeError(t("Field is required"))
      .required(t("Field is required")),
  });

  const sailorCrewRequiredOneValidation = Yup.object().shape({
    start_dt: Yup.date().required(t("Field is required")).nullable(),
    specialty: Yup.object({
      id: Yup.string().required(t("Field is required")).nullable(),
    })
      .typeError(t("Field is required"))
      .required(t("Field is required")),
    leave_dt: Yup.date().nullable(),
    leave_place: Yup.object().nullable(),
    leave_reason: Yup.object().nullable(),
  });

  const [currentValidation, setCurrentValidation] = useState(
    sailorCrewRequiredOneValidation
  );

  //change validatorSchema based on fields touched
  const changeValidator = (propertyName, value, setFieldValue) => {
    setFieldValue(propertyName, value);

    if (value) {
      setCurrentValidation(sailorFormRequireAllValidation);
    } else {
      setCurrentValidation(sailorCrewRequiredOneValidation);
    }
  };
  // Fetch Leave Reasons
  const { data: leaveReasons } = useQuery("leaveReasons", fetchLeaveReasons);

  // Fetch Ports Names
  const { data: portNames } = useQuery("portNames", fetchPortNames);

  // Fetch Sailor Specializations
  const { data: sailorSpecialization } = useQuery(
    "sailorSpecialization",
    fetchSailorSpecialization
  );

  // Mutation for creating data
  const { mutate } = useMutation(createShipRecordDetailsService, {
    onSuccess: (data) => {
      refetch();
      finishStepper();
      close();
    },
    onError: (error) => {
      switch (error) {
        default: {
          setError(t("A network error occured. Please try again later"));
        }
      }
    },
  });

  const handleCreateItem = (values) => {
    let createData;
    const {
      leave_dt,
      start_dt,
      leave_reason,
      leave_place,
      start_place,
      sailor_name,
      ameth,
      specialty,
      ...rest
    } = values;

    if (leave_dt) {
      createData = {
        ...rest,
        start_dt: dayjs(start_dt).format("YYYY-MM-DD"),
        specialty_id: specialty.id,
        leave_place_id: leave_place.id,
        start_place_id: start_place.id,
        leave_reason_id: leave_reason.id,
        leave_dt: dayjs(leave_dt).format("YYYY-MM-DD"),
      };
    } else {
      createData = {
        ...rest,
        start_dt: dayjs(start_dt).format("YYYY-MM-DD"),
        start_place_id: start_place.id,
        specialty_id: specialty.id,
        leave_place_id: null,
        leave_reason_id: null,
        leave_dt: null,
      };
    }

    mutate({ items: JSON.stringify([createData]) });
  };

  // Initial values
  const initialValues = {
    sailor_name: selectedSailor.s_first_name + " " + selectedSailor.s_last_name,
    ameth: selectedSailor.s_ameth,
    specialty: null,
    ship_record_hd_id: parseInt(crewlist),
    sailor_id: selectedSailor?.s_id,
    start_dt: null,
    start_place: null,
    leave_reason: null, //leave reason
    leave_place: null, //port
    leave_dt: null,
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={currentValidation}
        enableReinitialize={true}
        onSubmit={(values, { setSubmitting }) => {
          handleCreateItem(values);
          setSubmitting(false);
        }}
        innerRef={ref}
      >
        {({ setFieldValue, errors, touched }) => (
          <Form>
            <DialogContent>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <Field
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    name="sailor_name"
                    type="text"
                    disabled
                    label={t("Full Sailor Name")}
                    component={FormikTextField}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    name="ameth"
                    type="text"
                    disabled
                    label={t("Ameth")}
                    component={FormikTextField}
                  />
                </Grid>
              </Grid>
              <Field
                name="specialty"
                component={FormikAutocomplete}
                options={sailorSpecialization || []}
                getOptionLabel={(options) => options.name || ""}
                renderInput={(params) => (
                  <TextField
                    error={touched["specialty"] && !!errors["specialty"]}
                    helperText={touched["specialty"] && errors["specialty"]}
                    {...params}
                    label={t("Sailor Specializations")}
                    variant="outlined"
                    margin="normal"
                  />
                )}
              />

              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <Field
                    margin="normal"
                    name="start_dt"
                    type="text"
                    fullWidth
                    label={t("Nautical Recruitment Date")}
                    component={FormikKeyboardDatePicker}
                    showTodayButton
                    inputVariant="outlined"
                    format="dd/MM/yyyy"
                    todayLabel={t("Today")}
                    cancelLabel={t("Cancel")}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    name="start_place"
                    component={FormikAutocomplete}
                    options={portNames || []}
                    getOptionLabel={(options) => options.name || ""}
                    renderInput={(params) => (
                      <TextField
                        error={
                          touched["start_place"] && !!errors["start_place"]
                        }
                        helperText={
                          touched["start_place"] && errors["start_place"]
                        }
                        {...params}
                        label={t("Start Place")}
                        variant="outlined"
                        margin="normal"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Field
                    margin="normal"
                    name="leave_dt"
                    type="text"
                    fullWidth
                    label={t("Leave Date")}
                    component={FormikKeyboardDatePicker}
                    showTodayButton
                    inputVariant="outlined"
                    format="dd/MM/yyyy"
                    todayLabel={t("Today")}
                    cancelLabel={t("Cancel")}
                    onChange={(value) =>
                      changeValidator("leave_dt", value, setFieldValue)
                    }
                  />
                </Grid>
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={6}>
                  <Field
                    name="leave_reason"
                    component={FormikAutocomplete}
                    options={leaveReasons || []}
                    getOptionLabel={(options) => options.name || ""}
                    onChange={(e, value) =>
                      changeValidator("leave_reason", value, setFieldValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        error={
                          touched["leave_reason"] && !!errors["leave_reason"]
                        }
                        helperText={
                          touched["leave_reason"] && errors["leave_reason"]
                        }
                        {...params}
                        label={t("Leave Reason")}
                        variant="outlined"
                        margin="normal"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Field
                    name="leave_place"
                    component={FormikAutocomplete}
                    options={portNames || []}
                    getOptionLabel={(options) => options.name || ""}
                    onChange={(e, value) =>
                      changeValidator("leave_place", value, setFieldValue)
                    }
                    renderInput={(params) => (
                      <TextField
                        error={
                          touched["leave_place"] && !!errors["leave_place"]
                        }
                        helperText={
                          touched["leave_place"] && errors["leave_place"]
                        }
                        {...params}
                        label={t("Leave Place")}
                        variant="outlined"
                        margin="normal"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </DialogContent>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default forwardRef(AddSailorDetails);
